import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

interface Point {
  x: number;
  y: number;
}

const COLS = 30;
const ROWS = 15;
const CELL = 20;
const TICK_MS = 120;
const START_SNAKE: Point[] = [{ x: 10, y: 7 }];
const START_DIRECTION: Point = { x: 1, y: 0 };

/**
 * Hidden "SNAKE.EXE" easter egg: a classic snake game rendered on a
 * <canvas>, styled to match the IBM CRT terminal theme. Only reachable
 * while "terminal mode" (see App.terminalMode) is active, and triggered
 * by typing "snake" anywhere on the page.
 */
@Component({
  selector: 'app-snake-game',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onKeydown($event)'
  },
  template: `
    <div class="snake-panel">
      <div class="snake-header">
        <span class="snake-title">*** SNAKE.EXE ***</span>
        <span class="snake-score">SCORE: {{ score() }}</span>
        <button class="snake-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="snake-canvas"></canvas>
      @if (gameOver()) {
        <div class="snake-gameover">GAME OVER — SCORE {{ score() }} — PRESS ENTER TO RESTART</div>
      } @else {
        <div class="snake-hint">USE ARROW KEYS / WASD TO MOVE</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      height: 50vh;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.94);
      border-top: 2px solid #33ff66;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
    }
    .snake-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
    }
    .snake-header {
      display: flex;
      gap: 1.5rem;
      align-items: center;
      color: #33ff66;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-size: 0.875rem;
    }
    .snake-score {
      color: #ffb000;
    }
    .snake-close {
      background: none;
      border: 1px solid #33ff66;
      color: #33ff66;
      padding: 0.25rem 0.6rem;
      cursor: pointer;
      font-family: inherit;
      font-size: 0.75rem;
      border-radius: 0;
    }
    .snake-close:hover {
      background: #33ff66;
      color: #000000;
    }
    .snake-canvas {
      border: 1px solid #1a4d1a;
      image-rendering: pixelated;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.25);
    }
    .snake-gameover {
      color: #ffb000;
      font-size: 0.875rem;
      letter-spacing: 0.05em;
      text-align: center;
      animation: snake-blink 1s steps(1) infinite;
    }
    .snake-hint {
      color: rgba(51, 255, 102, 0.5);
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      text-align: center;
    }
    @keyframes snake-blink {
      50% { opacity: 0; }
    }
  `]
})
export class SnakeGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');

  close = output<void>();

  score = signal(0);
  gameOver = signal(false);

  readonly canvasWidth = COLS * CELL;
  readonly canvasHeight = ROWS * CELL;

  private snake: Point[] = [...START_SNAKE];
  private direction: Point = { ...START_DIRECTION };
  private nextDirection: Point = { ...START_DIRECTION };
  private food: Point = this.randomFood();
  private ctx?: CanvasRenderingContext2D;
  private intervalId?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.ctx = this.canvasRef().nativeElement.getContext('2d') ?? undefined;
    this.startLoop();
  }

  ngOnDestroy(): void {
    this.stopLoop();
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        this.setDirection(0, -1);
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        this.setDirection(0, 1);
        break;
      case 'ArrowLeft':
      case 'a':
      case 'A':
        this.setDirection(-1, 0);
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        this.setDirection(1, 0);
        break;
      case 'Escape':
        this.requestClose();
        break;
      case 'Enter':
        if (this.gameOver()) {
          this.restart();
        }
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  requestClose(): void {
    this.close.emit();
  }

  private setDirection(x: number, y: number): void {
    // Ignore direct reversals into the snake's own body.
    if (this.direction.x === -x && this.direction.y === -y) {
      return;
    }
    this.nextDirection = { x, y };
  }

  private startLoop(): void {
    this.stopLoop();
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
    this.draw();
  }

  private stopLoop(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  private tick(): void {
    this.direction = this.nextDirection;
    const head = this.snake[0];
    const newHead: Point = { x: head.x + this.direction.x, y: head.y + this.direction.y };

    const hitWall = newHead.x < 0 || newHead.x >= COLS || newHead.y < 0 || newHead.y >= ROWS;
    const hitSelf = this.snake.some(segment => segment.x === newHead.x && segment.y === newHead.y);

    if (hitWall || hitSelf) {
      this.endGame();
      return;
    }

    this.snake.unshift(newHead);
    if (newHead.x === this.food.x && newHead.y === this.food.y) {
      this.score.update(current => current + 10);
      this.food = this.randomFood();
    } else {
      this.snake.pop();
    }

    this.draw();
  }

  private endGame(): void {
    this.stopLoop();
    this.gameOver.set(true);
  }

  private restart(): void {
    this.snake = [...START_SNAKE];
    this.direction = { ...START_DIRECTION };
    this.nextDirection = { ...START_DIRECTION };
    this.food = this.randomFood();
    this.score.set(0);
    this.gameOver.set(false);
    this.startLoop();
  }

  private randomFood(): Point {
    let point: Point;
    do {
      point = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) };
    } while (this.snake.some(segment => segment.x === point.x && segment.y === point.y));
    return point;
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

    ctx.strokeStyle = 'rgba(51, 255, 102, 0.08)';
    for (let x = 0; x <= COLS; x++) {
      ctx.beginPath();
      ctx.moveTo(x * CELL, 0);
      ctx.lineTo(x * CELL, this.canvasHeight);
      ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL);
      ctx.lineTo(this.canvasWidth, y * CELL);
      ctx.stroke();
    }

    ctx.fillStyle = '#ffb000';
    ctx.fillRect(this.food.x * CELL + 2, this.food.y * CELL + 2, CELL - 4, CELL - 4);

    ctx.fillStyle = '#33ff66';
    this.snake.forEach((segment, index) => {
      ctx.globalAlpha = index === 0 ? 1 : 0.85;
      ctx.fillRect(segment.x * CELL + 1, segment.y * CELL + 1, CELL - 2, CELL - 2);
    });
    ctx.globalAlpha = 1;
  }
}
